import { coreActions } from '@sonar-software/sonar-core';
import { createSonarSelector } from '../lib/selectors';
import { SonarObjectType } from '@sonar-software/sonar-ts-types';
import { useAction } from './useAction';
import { useSonarSelector } from './useSonarSelector';

import { useCallback, useMemo } from 'react';

export const globalSelectionSelector = createSonarSelector((state) => state.core.selections.read);

export type GetGlobalSelectionOptions = {
	namespace?: string;
	types?: SonarObjectType[];
	excludeTypes?: SonarObjectType[];
};

export interface UseGlobalSelectResult {
	addSelection: typeof coreActions.addCoreSelections;
	getGlobalSelection: (options?: GetGlobalSelectionOptions) => number[];
	globalSelection: number[];
	removeSelection: typeof coreActions.removeCoreSelections;
	totalCount: number;
}

export function useGlobalSelect(): UseGlobalSelectResult {
	const coreSelection = useSonarSelector(globalSelectionSelector).data;

	const getGlobalSelection = useCallback((options?: GetGlobalSelectionOptions) => {
		const typesSet = new Set(options?.types);
		const excludeTypesSet = new Set(options?.excludeTypes);
		const result = [];
		const namespaceSelections = coreSelection?.[ options?.namespace ?? 'global' ] ?? {};

		for (const [ id, { sonarObjectType } ] of Object.entries(namespaceSelections)) {
			if (
				(!options?.types && !options?.excludeTypes) ||
				typesSet.has(sonarObjectType) ||
				!excludeTypesSet.has(sonarObjectType)
			) {
				result.push(Number(id));
			}
		}

		return result;
	}, [ coreSelection ]);

	const globalSelection = useMemo(() => getGlobalSelection(), [ getGlobalSelection ]);

	const addSelection = useAction(coreActions.addCoreSelections);
	const removeSelection = useAction(coreActions.removeCoreSelections);

	return {
		addSelection,
		getGlobalSelection,
		globalSelection,
		removeSelection,
		totalCount: globalSelection.length
	};
}
